import { Link } from "react-router-dom";
import {
  Bookmark,
  Trash2,
  ExternalLink,
  ClipboardList,
  CheckCircle,
  Layers3,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { useBookmarks } from "../hooks/useBookmarks";

type BookmarkType = "issue" | "card" | "result";

const typeIcons: Record<BookmarkType, React.ComponentType<{ className?: string }>> = {
  issue: ExternalLink,
  card: ClipboardList,
  result: CheckCircle,
};

const typeLabels: Record<BookmarkType, string> = {
  issue: "Issue",
  card: "Card",
  result: "Diagnosis",
};

function BluePanel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function StatPill({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
      <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-dtf-text-muted">
        <span className="text-dtf-accent">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-base font-semibold text-white">{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <BluePanel>
      <div className="px-6 py-12 text-center sm:px-10 sm:py-16">
        <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-3xl border border-white/10 bg-black/20">
          <Bookmark className="h-10 w-10 text-dtf-blue" />
        </div>

        <h3 className="mb-2 text-2xl font-bold text-white">No Bookmarks Yet</h3>

        <p className="mx-auto mb-8 max-w-2xl text-dtf-text-muted leading-8">
          Save issues, cards, and diagnosis results as you work through the app so you can jump
          back to them fast later.
        </p>

        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/issues"
            className="inline-flex items-center gap-2 rounded-2xl bg-dtf-accent px-5 py-3 font-semibold text-dtf-dark transition hover:bg-dtf-accent-hover"
          >
            Browse Issues
            <ArrowRight className="h-4 w-4" />
          </Link>

          <Link
            to="/diagnosis"
            className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-5 py-3 font-medium text-white transition hover:border-dtf-blue/30 hover:bg-black/30"
          >
            Start Diagnosis
          </Link>
        </div>
      </div>
    </BluePanel>
  );
}

type BookmarkItem = {
  id: string;
  type: BookmarkType;
  title: string;
  timestamp: string;
};

function BookmarkCard({
  bookmark,
  onRemove,
}: {
  bookmark: BookmarkItem;
  onRemove: (id: string, type: BookmarkType) => void;
}) {
  const Icon = typeIcons[bookmark.type];

  const getLink = () => {
    switch (bookmark.type) {
      case "issue":
        return `/issues/${bookmark.id}`;
      case "card":
        return "/cards";
      case "result":
        return "/diagnosis";
      default:
        return "/";
    }
  };

  return (
    <div className="group relative overflow-hidden rounded-[26px] border border-white/10 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.14),transparent_48%),linear-gradient(180deg,rgba(9,21,44,0.98)_0%,rgba(7,15,31,0.98)_100%)] p-5 transition duration-200 hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:shadow-[0_0_26px_rgba(37,99,235,0.18)]">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.06),transparent_35%,transparent_65%,rgba(37,99,235,0.04))]" />

      <div className="relative flex items-start gap-4">
        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-dtf-blue transition group-hover:border-dtf-blue/30 group-hover:bg-dtf-blue/10">
          <Icon className="h-6 w-6" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-2 inline-flex rounded-full border border-dtf-accent/20 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-dtf-accent">
            {typeLabels[bookmark.type]}
          </div>

          <h3 className="truncate text-xl font-bold tracking-tight text-white">
            {bookmark.title}
          </h3>

          <p className="mt-2 text-sm text-dtf-text-muted">
            Saved {new Date(bookmark.timestamp).toLocaleDateString()}
          </p>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          <Link
            to={getLink()}
            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-dtf-text-muted transition hover:border-dtf-blue/30 hover:bg-black/30 hover:text-white"
            title="Open bookmark"
          >
            <ExternalLink className="h-5 w-5" />
          </Link>

          <button
            onClick={() => onRemove(bookmark.id, bookmark.type)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-dtf-text-muted transition hover:border-red-500/30 hover:bg-red-500/10 hover:text-red-400"
            title="Remove bookmark"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function Bookmarks() {
  const { bookmarks, removeBookmark } = useBookmarks();

  const typedBookmarks = bookmarks as BookmarkItem[];

  const issueCount = typedBookmarks.filter((bookmark) => bookmark.type === "issue").length;
  const cardCount = typedBookmarks.filter((bookmark) => bookmark.type === "card").length;
  const diagnosisCount = typedBookmarks.filter((bookmark) => bookmark.type === "result").length;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <BluePanel className="mb-6">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-accent/20 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
                <Bookmark className="h-3.5 w-3.5" />
                Bookmarks
              </div>

              <h1 className="mb-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                Your Saved Items
              </h1>

              <p className="text-lg leading-8 text-dtf-text">
                Keep important issues, cards, and diagnosis results in one place for fast access
                while you work.
              </p>
            </div>

            <div className="grid gap-3 sm:min-w-[320px] sm:grid-cols-2">
              <StatPill
                icon={<Layers3 className="h-4 w-4" />}
                label="Total Saved"
                value={`${typedBookmarks.length}`}
              />
              <StatPill
                icon={<Sparkles className="h-4 w-4" />}
                label="Quick Access"
                value="Enabled"
              />
            </div>
          </div>
        </div>
      </BluePanel>

      <div className="mb-6 grid gap-4 md:grid-cols-3">
        <BluePanel>
          <div className="p-5">
            <div className="mb-2 text-sm text-dtf-text-muted">Issues</div>
            <div className="text-3xl font-bold text-white">{issueCount}</div>
          </div>
        </BluePanel>

        <BluePanel>
          <div className="p-5">
            <div className="mb-2 text-sm text-dtf-text-muted">Cards</div>
            <div className="text-3xl font-bold text-white">{cardCount}</div>
          </div>
        </BluePanel>

        <BluePanel>
          <div className="p-5">
            <div className="mb-2 text-sm text-dtf-text-muted">Diagnosis Results</div>
            <div className="text-3xl font-bold text-white">{diagnosisCount}</div>
          </div>
        </BluePanel>
      </div>

      {typedBookmarks.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <BluePanel className="mb-6">
            <div className="flex items-center justify-between gap-4 px-6 py-5">
              <div>
                <div className="text-sm text-dtf-text-muted">Showing results</div>
                <div className="text-2xl font-bold text-white">
                  {typedBookmarks.length} saved item{typedBookmarks.length === 1 ? "" : "s"}
                </div>
              </div>

              <div className="rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-dtf-blue">
                Bookmarks visible
              </div>
            </div>
          </BluePanel>

          <div className="grid gap-5 lg:grid-cols-2">
            {typedBookmarks.map((bookmark) => (
              <BookmarkCard
                key={`${bookmark.type}-${bookmark.id}`}
                bookmark={bookmark}
                onRemove={removeBookmark}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}